import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  RefreshCw,
  CheckCheck,
  Search,
  Clock,
  ChevronRight,
  CalendarRange,
  User,
} from "lucide-react";
import { formatLocalDateTime } from "../lib/constants";

const REFRESH_MS = 60000;

const RANGES = {
  today: { label: "Today", days: 0 },
  week: { label: "This week", days: 7 },
  all: { label: "All time", days: null },
};

function getSince(rangeKey) {
  if (rangeKey === "all") return null;
  const now = new Date();
  if (rangeKey === "today") {
    now.setHours(0, 0, 0, 0);
  } else {
    now.setDate(now.getDate() - 7);
    now.setHours(0, 0, 0, 0);
  }
  return now.toISOString();
}

export default function CompletedTasks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [range, setRange] = useState("today");
  const [q, setQ] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);

  const params = useMemo(() => {
    const p = { prep_status: "Completed", limit: 1000 };
    const since = getSince(range);
    if (since) p.updated_since = since;
    return p;
  }, [range]);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/shipments", { params });
      // Sort by Last_Updated desc — most recently completed first
      const sorted = [...data].sort(
        (a, b) => new Date(b.Last_Updated).getTime() - new Date(a.Last_Updated).getTime()
      );
      setItems(sorted);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const filtered = items.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      s.MR_Number.toLowerCase().includes(needle) ||
      (s.Contents || "").toLowerCase().includes(needle) ||
      (s.Project_Station || "").toLowerCase().includes(needle) ||
      (s.Assigned_Crew_Leader || "").toLowerCase().includes(needle) ||
      (s.Last_Updated_By || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-green-700 flex items-center gap-2">
            <CheckCheck className="h-4 w-4" />
            Yard Runner / Completed
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight">
            Completed Tasks
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Materials marked completed by the Yard Runner.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="completed-refresh-btn"
          className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {lastRefresh && (
        <div className="text-xs text-slate-500 mb-4 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Last refreshed {formatLocalDateTime(lastRefresh.toISOString())} · {filtered.length} item
          {filtered.length === 1 ? "" : "s"}
        </div>
      )}

      {/* Range tabs */}
      <div
        className="grid grid-cols-3 gap-1 p-1 bg-slate-100 border-2 border-slate-200 rounded-sm mb-3"
        data-testid="completed-range-tabs"
      >
        {Object.entries(RANGES).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setRange(k)}
            data-testid={`completed-range-${k}`}
            className={`rg-tap rounded-sm font-bold uppercase tracking-wide text-xs sm:text-sm flex items-center justify-center gap-1.5 ${
              range === k
                ? "bg-slate-900 text-white"
                : "bg-transparent text-slate-700 hover:bg-white"
            }`}
          >
            <CalendarRange className="h-3.5 w-3.5" />
            {v.label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="completed-search"
          placeholder="Search MR, contents, station, crew leader, who completed…"
          className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white text-base focus:border-slate-900 outline-none"
        />
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="completed-empty">
          <div className="font-display text-xl text-slate-900 uppercase mb-1">
            Nothing completed {range === "today" ? "today" : range === "week" ? "this week" : "yet"}
          </div>
          <div className="text-sm text-slate-600">
            Once an item is marked Completed it'll show up here.
          </div>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <ul className="md:hidden space-y-3" data-testid="completed-mobile-list">
            {filtered.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/shipments/${s.id}`}
                  data-testid={`completed-row-${s.MR_Number}`}
                  className="block rg-card p-4 hover:border-slate-900 border-l-[6px] border-l-green-600"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="font-mono text-lg font-bold text-slate-900">
                      {s.MR_Number}
                    </div>
                    <span className="rg-pill text-green-700 bg-green-50">Completed</span>
                  </div>
                  <div className="text-sm text-slate-700 font-bold">{s.Project_Station}</div>
                  {s.Assigned_Crew_Leader && (
                    <div className="text-xs mt-1 flex items-center gap-1 text-slate-700">
                      <User className="h-3.5 w-3.5 text-slate-500" />
                      Crew: <span className="font-bold">{s.Assigned_Crew_Leader}</span>
                    </div>
                  )}
                  {s.Contents && (
                    <div className="text-sm text-slate-700 mt-1 line-clamp-2">{s.Contents}</div>
                  )}
                  <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                    <span>
                      Completed by{" "}
                      <span className="text-slate-800 font-bold">
                        {s.Last_Updated_By || "—"}
                      </span>
                    </span>
                    <span>{formatLocalDateTime(s.Last_Updated)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          <div className="hidden md:block rg-card overflow-x-auto" data-testid="completed-desktop-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 bg-slate-50">
                  <th className="py-3 px-3">MR</th>
                  <th className="py-3 px-3">Project / Station</th>
                  <th className="py-3 px-3">Crew Leader</th>
                  <th className="py-3 px-3">Completed</th>
                  <th className="py-3 px-3">Contents</th>
                  <th className="py-3 px-3">Who Completed</th>
                  <th className="py-3 px-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`completed-tr-${s.MR_Number}`}
                  >
                    <td className="py-2 px-3">
                      <Link
                        to={`/shipments/${s.id}`}
                        className="font-mono font-bold text-slate-900 hover:underline"
                      >
                        {s.MR_Number}
                      </Link>
                    </td>
                    <td className="py-2 px-3 text-slate-700">{s.Project_Station}</td>
                    <td className="py-2 px-3 text-slate-700 font-bold">
                      {s.Assigned_Crew_Leader || (
                        <span className="text-slate-400 font-normal">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">
                      {formatLocalDateTime(s.Last_Updated)}
                    </td>
                    <td className="py-2 px-3 text-slate-700 max-w-[260px] truncate">
                      {s.Contents || "—"}
                    </td>
                    <td className="py-2 px-3 text-slate-700">{s.Last_Updated_By || "—"}</td>
                    <td className="py-2 px-3">
                      <Link to={`/shipments/${s.id}`} className="text-slate-400 hover:text-slate-900">
                        <ChevronRight className="h-5 w-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
